import { useRef, useState } from "react";
import { Typography } from "antd";
import InModal from "../../controls/InModal";
import ModalFooter from "../../controls/ModalFooter";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import BandListEditor, {
  BandListEditorHandle,
  toBandRows,
} from "../../controls/BandListEditor";
import { getProblemMessage } from "../../utils/http";
import { MetricDefinition } from "../../repository/data.default";
import {
  addMetricBand,
  updateMetricBand,
  deleteMetricBand,
} from "../../repository/data.repository";

export interface MetricBandsDialogProps {
  readonly open?: boolean;
  readonly definition?: MetricDefinition;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

// Manages a metric definition's bands (thresholds + guidance) on the shared BandListEditor. Bands live top-level
// (no parent form), so this thin dialog owns the Save that flushes the editor buffer against the definition uid.
function MetricBandsDialog(props: MetricBandsDialogProps) {
  const editorRef = useRef<BandListEditorHandle>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const initial = toBandRows(
    props.definition?.bands ?? [],
    (b) => b.upperThreshold,
  );

  function onSubmit(): void {
    if (!props.definition) return;
    setLoading(true);
    setErrorMessage(null);
    (editorRef.current?.persist(props.definition.uid) ?? Promise.resolve())
      .then(() => {
        setLoading(false);
        props.onFinish?.();
      })
      .catch((e) => {
        setLoading(false);
        setErrorMessage(getProblemMessage(e, "Something went wrong, try again"));
      });
  }

  return (
    <InModal
      open={props.open}
      size="large"
      title={`Bands — ${props.definition?.name ?? "Metric"}`}
      onCancel={props.onCancel}
      actions={
        <ModalFooter
          mode="update"
          loading={loading}
          valid
          submitText="Save"
          onCancel={props.onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      <Typography.Text type="secondary">
        Each band's interpretation &amp; recommended action are surfaced in the
        exported report PDF.
      </Typography.Text>
      <BandListEditor
        ref={editorRef}
        mode="update"
        ownerKeyName="metricDefinitionUid"
        thresholdKeyName="upperThreshold"
        initial={initial}
        columns={[
          { key: "label", label: "Label", type: "text" },
          { key: "threshold", label: "≤ Rate %", type: "number" },
          {
            key: "readinessMultiplier",
            label: "Readiness ×",
            type: "number",
          },
          { key: "color", label: "Color", type: "color" },
          { key: "interpretation", label: "Interpretation", type: "textarea" },
          {
            key: "recommendedAction",
            label: "Recommended action",
            type: "textarea",
          },
        ]}
        onAdd={addMetricBand}
        onUpdate={updateMetricBand}
        onDelete={deleteMetricBand}
      />
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default MetricBandsDialog;
